import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import "./ProgramsBanner.css";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
import {
  FaHandHoldingHeart,
  FaUsers,
  FaSchool,
  FaUserCheck,
  FaBookOpen,
  FaMapMarkerAlt,
} from "react-icons/fa";
import "./ChildLabourPrevention.css";

// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const ChildLabourPrevention = () => {
  // Data for the initiatives section for cleaner code
  const initiatives = [
    {
      icon: FaSchool,
      title: "Launch Child Labour Prevention Centers",
      text: "Establishing centers in vulnerable areas to provide safe learning spaces.",
    },
    {
      icon: FaUserCheck,
      title: "Identify At-Risk Children",
      text: "Identifying children who are not attending school and at risk of child labor.",
    },
    {
      icon: FaBookOpen,
      title: "Integrate into Education",
      text: "Managing specialized centers to integrate children into the mainstream education system.",
    },
    {
      icon: FaMapMarkerAlt,
      title: "Expand Across Odisha",
      text: "Operating and supporting centers across 4 districts of Odisha.",
    },
  ];

  const { openDonation } = useDonation();
  const navigate = useNavigate();

  return (
    <>
      <Header />

      {/* Hero Section (unchanged) */}
      <section className="page-header">
        <div
          className="page-header__bg"
          style={{
            backgroundImage: "url(/assets/images/backgrounds/page-header-bg.jpg)",
          }}
        ></div>
        <div className="shape1 float-bob-x">
          <img src="assets/images/shapes/page-header-shape1.png" alt="" />
        </div>
        <div className="shape2">
          <img src="assets/images/shapes/page-header-shape2.png" alt="" />
        </div>
        <Container>
          <div className="page-header__inner text-center">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Child Labour Prevention
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{ color: "#dad2d0", fontSize: "22px", marginTop: "20px" }}
            >
              Vikash works to prevent child labor by raising awareness, providing education, and supporting families to ensure children can learn and thrive in safe environments.
            </motion.p>
          </div>
        </Container>
      </section>

      {/* ================= Introduction ================= */}
      <section className="clp-intro">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="align-items-center g-5">
              <motion.div className="col-lg-5" variants={staggerItem}>
                <span className="clp-eyebrow__dash" />
                <h2 className="clp-heading">
                  A Mission Born
                  <br />
                  from Crisis
                </h2>
                <p className="clp-intro__text">
                  In the aftermath of the 1999 Super Cyclone, Vikash was
                  assigned the critical work of launching the Child Labour
                  Prevention Center in Gop Block, Puri district.
                </p>

                <div className="clp-callout">
                  <span className="clp-callout__icon">
                    <FaHandHoldingHeart />
                  </span>
                  <p className="clp-callout__text">
                    Our mission recognized that vulnerable children not
                    attending school are potential child laborers. We have
                    since managed child labor centers across 4 districts of
                    Odisha to integrate these children back into the
                    mainstream education system.
                  </p>
                </div>
              </motion.div>

              <motion.div className="col-lg-7" variants={staggerItem}>
                <div className="clp-imageStack">
                  <span className="clp-imageStack__dots" />
                  <span className="clp-imageStack__square" />
                  <img
                    src="/assets/images/Subsections/cl1.jpg"
                    alt="Child receiving education"
                    className="clp-imageStack__img clp-imageStack__img--back"
                  />
                  <img
                    src="/assets/images/Subsections/cl2.jpg"
                    alt="Children in a safe learning environment"
                    className="clp-imageStack__img clp-imageStack__img--front"
                  />
                </div>
              </motion.div>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* ================= Key Initiatives ================= */}
      <section className="clp-approach">
        <span className="clp-approach__dots" aria-hidden="true" />
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="clp-approach__header"
          >
            <span className="clp-approach__badge">
              <FaUsers />
            </span>
            <h2 className="clp-heading clp-heading--center">
              Our Approach to <span>Prevention</span>
            </h2>
            <span className="clp-heading__underline" />
            <p className="clp-approach__lead">
              We focus on proactive intervention and educational integration
              to protect children's futures.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="g-4 justify-content-center">
              {initiatives.map((item) => {
                const Icon = item.icon;
                return (
                  <Col md={6} lg={3} key={item.title}>
                    <motion.div variants={staggerItem} className="clp-card">
                      <span className="clp-card__icon">
                        <Icon />
                      </span>
                      <h5 className="clp-card__title">{item.title}</h5>
                      <p className="clp-card__text">{item.text}</p>
                    </motion.div>
                  </Col>
                );
              })}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* ================= CTA ================= */}
      <section className="clp-cta">
        <Container>
          <motion.div
            className="clp-cta__card"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <div className="clp-cta__imageWrap">
              <img
                src="/assets/images/Subsections/cl1.jpg"
                alt="Child writing"
                className="clp-cta__image"
              />
              <span className="clp-cta__dots" />
            </div>

            <div className="clp-cta__content">
              <span className="clp-cta__icon">
                <FaHandHoldingHeart />
              </span>
              <h2 className="clp-cta__title">Protect a Child's Future</h2>
              <span className="clp-cta__underline" />
              <p className="clp-cta__text">
                Your support helps us rescue children from labor and provide
                them with the education they deserve.
              </p>
              <div className="clp-cta__actions">
                <button
                  type="button"
                  className="clp-btn clp-btn--solid"
                  onClick={openDonation}
                >
                  <FaHandHoldingHeart /> Donate
                </button>
                <button
                  type="button"
                  className="clp-btn clp-btn--outline"
                  onClick={() => navigate("/interns")}
                >
                  <FaUsers /> Join Us
                </button>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default ChildLabourPrevention;